import { Box, Flex } from '@chakra-ui/react';
import InputField from 'components/forms/InputField';
import TextareaField from 'components/forms/TextareaField';
import PortfolioContents from '../PortfolioContents';
import { PCW_STEPS } from '../constants';
import CheckboxField from 'components/forms/CheckboxField';
import { FormattedMessage } from 'react-intl';
import { useMemo } from 'react';
import { Book } from 'types/connection';
import { StepDetails } from '../StepDetails';
import { useWizardContext } from 'components/wizard/hooks/useWizardContext';
import useCommonToast from 'hooks/useCommonToast';
import { useWizardSubmit } from 'components/wizard/hooks/useWizardSubmit';
import { useWizardState } from 'components/wizard/hooks/useWizardState';
import useHistoryPush from 'hooks/useHistoryPush';

export function SummaryStep() {
    const historyPush = useHistoryPush();
    const { toast } = useCommonToast();
    const { onAfterSubmit } = useWizardContext();

    const { handleSubmit } = useWizardSubmit();
    const { formData, currentStep, metadata } = useWizardState();

    const book = useMemo(() => {
        return metadata?.books?.find((b: Book) => b.id === formData?.[PCW_STEPS.PCW_PORTFOLIO_BASICS_STEP_ID]?.bookId);
    }, [metadata, formData]);

    const mainMarketModel = useMemo(() => {
        return metadata?.mainMarketModels?.find(
            (m: any) => m.id === formData?.[PCW_STEPS.PCW_PORTFOLIO_BASICS_STEP_ID]?.mainMarketModel
        );
    }, [metadata, formData]);

    const submit = async () => {
        const result = await handleSubmit();

        if (result.success && result.data) {
            toast({
                title: 'Portfolio Created',
                message: 'Your portfolio has been created successfully.',
                status: 'success',
                isClosable: true,
            });

            const search = new URLSearchParams();
            search.append('portfolioId', result.data);

            historyPush({
                pathname: '/portfolios',
                search,
            });

            if (onAfterSubmit) {
                onAfterSubmit();
            }
        } else {
            toast({
                title: 'Creation Failed',
                message: result.error || 'Failed to create portfolio.',
                status: 'error',
                isClosable: true,
                duration: 8000,
            });
        }
    };

    return (
        <form id={`form-${currentStep.id}`} onSubmit={submit}>
            <Flex h="full" w="full" flexDir="column">
                <Flex gap={2}>
                    <Box flex="1">
                        <StepDetails />
                        <InputField
                            name="portfolioName"
                            type="text"
                            label="Portfolio Name"
                            isDisabled
                            mb={5}
                            value={formData?.[PCW_STEPS.PCW_PORTFOLIO_BASICS_STEP_ID]?.portfolioName}
                        />
                        <CheckboxField
                            id="isMaster"
                            name="isMaster"
                            mb={5}
                            isDisabled={true}
                            onChange={undefined}
                            isChecked={!!formData?.[PCW_STEPS.PCW_PORTFOLIO_BASICS_STEP_ID]?.isMaster}
                            isInvalid={undefined}
                            isWarning={undefined}
                            isIndeterminate={undefined}
                        >
                            <FormattedMessage id="common_master" />
                        </CheckboxField>
                        <InputField
                            name="parentPortfolio"
                            type="text"
                            label="Parent Portfolio"
                            isDisabled
                            mb={5}
                            value={formData?.[PCW_STEPS.PCW_PORTFOLIO_BASICS_STEP_ID]?.parentPortfolio}
                        />
                        <InputField name="book" type="text" label="Book" isDisabled mb={5} value={book?.name} />
                        <InputField
                            name="mainMarketModel"
                            type="text"
                            label="Main Market Model"
                            isDisabled
                            mb={5}
                            value={mainMarketModel?.description}
                        />
                        <TextareaField
                            name="description"
                            label="Description"
                            placeholder="Description"
                            mb={5}
                            value={formData?.[PCW_STEPS.PCW_PORTFOLIO_BASICS_STEP_ID]?.description}
                            onChange={undefined}
                            isWarning={undefined}
                            isDisabled
                        />
                    </Box>
                    <Box flex="1">
                        <PortfolioContents />
                    </Box>
                </Flex>
            </Flex>
        </form>
    );
}
